import React, { memo, useCallback, useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { AvatarInfo, InfiniteScroll, SearchBar } from "components";
import EmptyContact from "./EmptyContact";
import { AppConstant, KeyConstant, LangConstant, SystemConstant } from "const";
import { Box, Divider, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useTranslation } from "react-i18next";
import AddingGroupButton from "./AddingGroupButton";
import clsx from "clsx";
import { useAccountList, useContactList, useCreateConversation } from "hooks";
import { TabPanel } from "@mui/base";
import { getSavedServer } from "utils/branch.utils";
import { StorageUtil } from "utils";

const AddingMessageTab = ({ onCloseDrawer }) => {
  const classes = useStyles();
  const searchRef = useRef();

  const { isFetching, contactList, isEnd: isEndContactList, getAllContactByName } = useContactList();
  const {
    accountList,
    queryPagination: { page: queryPage, totalPage },
    getAccountList,
  } = useAccountList();
  const { createWithPersonal } = useCreateConversation();

  const { t: getLabel } = useTranslation(LangConstant.NS_HOME);
  const chatObjContent = getLabel(LangConstant.OBJ_ADDING_CHAT, { returnObjects: true });
  const isBranchServer = getSavedServer().id !== SystemConstant.GLOBAL_BRANCH_ID;
  const currentAccountId = StorageUtil.getItem(KeyConstant.KEY_ACCOUNT_ID);

  const [viewingList, setViewingList] = useState([]);

  const onCreateSelect = useCallback(
    contact => () => {
      createWithPersonal(contact);
      onCloseDrawer();
    },
    [],
  );

  const handleChangeSearchInput = content => {
    searchRef.current = content;
    if (isBranchServer && content) {
      getAccountList({ keyword: content, page: 1 });
    } else {
      getAllContactByName(content);
    }
  };

  const handleLoadMore = () => {
    if (isBranchServer && searchRef.current && queryPage <= totalPage) {
      getAccountList({ keyword: searchRef.current, page: queryPage + 1 });
    } else if (!isEndContactList) {
      getAllContactByName(searchRef.current);
    }
  };

  useEffect(() => {
    const viewList = Boolean(isBranchServer && searchRef.current) ? accountList : contactList;
    setViewingList(viewList);
  }, [contactList, accountList]);

  const isEmptyChatting = false === isFetching && contactList.length === 0;

  return (
    <TabPanel value={AppConstant.CHATTING_TAB_MODE.message}>
      <Box className={classes.rootSpace}>
        <SearchBar onChange={handleChangeSearchInput} />
      </Box>
      {isEmptyChatting ? (
        <EmptyContact displayText={chatObjContent.emptyMessage} />
      ) : (
        <>
          <AddingGroupButton onComplete={onCloseDrawer} />
          <Typography variant="subtitle2" className={clsx(classes.subTitle, classes.rootSpace)}>
            {chatObjContent.people}
          </Typography>
          <InfiniteScroll className={classes.infiniteScroll} onScrollToBottom={handleLoadMore}>
            {viewingList.map((item, index) => (
              <Box key={`${item.avatarId}-${index}`}>
                {index > 0 && <Divider variant="inset" />}
                <AvatarInfo
                  avatar={{
                    avatarId: item.avatarId,
                    name: item.contactName || item.name,
                  }}
                  title={item.contactName || item.name || item.phone}
                  onClick={onCreateSelect(item)}
                  button={true}
                  sendToMe={item.id === currentAccountId}
                />
              </Box>
            ))}
          </InfiniteScroll>
        </>
      )}
    </TabPanel>
  );
};

AddingMessageTab.propTypes = {
  onCloseDrawer: PropTypes.func,
};

export default memo(AddingMessageTab);

const useStyles = makeStyles(theme => ({
  rootSpace: {
    paddingLeft: 16,
    paddingRight: 16,
  },

  subTitle: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },

  infiniteScroll: {
    height: "calc(100vh - 280px)",
    overflow: "auto",
    padding: "16px 14px",
  },
}));
